import { useEffect, useRef, useState } from "react";
import { signal, useSignal } from "@preact/signals-react";
import { NonEditableCell, ReactGrid } from "@silevis/reactgrid";
import dates from "tools/dates";
import Dialog from "componentsv2/modal/Dialog";
import CellData from "./CellData";
import EmployeeCell from "./EmployeeCell";
import ShiftForm from "./ShiftForm";
import ShiftList from "./ShiftList";
import { insertRecord, useStore } from "storev2/formStore";
import proxy from "api/proxy";
import { cn } from "tools/ui";
import {
  getColumnsSchema,
  getRowsSchema,
  getEmployeeRows,
  setHeaderMonth,
  setHeaderRow,
  // getMondayOfWeek,
  // getWeekDates,
  // getShiftHours,
  calculateEmployeeWeekHours,
  refreshWeeklyTotals,
  isChangeShiftWithinLimit,
} from "./helper";
import { getStorage } from "store/local";
import "./Sheet.css";
import {
  useMessage,
  MessageContainer,
} from "componentsv2/experimental/Message";

import { getColor } from "./helper";

const openModal = signal(false);
var negId = -1;

function Sheet({
  data,
  onSave,
  onDelete,
  shifts,
  layoutShift,
  selectedDays,
  weekHoursWork = 42,
  mealMap,
  holidays,
  refetch,
}) {
  const { messages, warning, success, error, hide } = useMessage();
  const { saveForm } = useStore();
  const { headDays, headMonths, records, events, extraShifts = [] } = data;

  const removedIdsRef = useRef(new Set());
  const lastCommitRef = useRef(null);
  useEffect(() => {
    removedIdsRef.current = new Set();
  }, [data]);

  function getDayEvents(employeeId, day) {
    const list = events?.[employeeId];
    if (!list) return [];
    return list.filter((ev) => {
      const start = ev.start_date;
      const end = ev.end_date;
      return day >= start && day <= end;
    });
  }

  function sortKindIds(kindIds) {
    return [...new Set(kindIds)].sort((a, b) => {
      const ka = shifts.get(a);
      const kb = shifts.get(b);
      const sa = ka?.start || "";
      const sb = kb?.start || "";
      if (sa !== sb) return sa < sb ? -1 : 1;
      return a - b;
    });
  }

  function getShiftCodes(kindIds) {
    return sortKindIds(kindIds)
      .map((k) => shifts.get(k)?.code || "")
      .filter(Boolean)
      .join("-");
  }

  function getDayShifts(employeeId, day, rowData, sectionId, extraById) {
    const list = [];
    const seen = new Set();
    const removed = removedIdsRef.current;
    if (rowData) {
      const shiftObj = JSON.parse(rowData);
      const id = shiftObj.id;
      const shiftKindId = parseInt(shiftObj.shift, 10);
      const inSection =
        !sectionId || extraById.get(id)?.section === sectionId;
      if (
        id &&
        shiftKindId &&
        inSection &&
        !removed.has(id) &&
        !seen.has(id)
      ) {
        seen.add(id);
        list.push({
          id: id,
          state: shiftObj.state,
          position: shiftObj.position,
          shift_kind: shiftKindId,
        });
      }
    }
    for (const s of extraShifts) {
      if (
        String(s.employee) === String(employeeId) &&
        s.shift_date === day &&
        (!sectionId || s.section === sectionId) &&
        !removed.has(s.id) &&
        !seen.has(s.id)
      ) {
        seen.add(s.id);
        list.push({
          id: s.id,
          state: s.state,
          position: s.position,
          shift_kind: s.shift_kind,
        });
      }
    }
    return list;
  }

  function getCellShifts(rec) {
    const res = [];
    const seen = new Set();
    for (const r of cellsDataRef.current.values()) {
      if (
        String(r.employee) === String(rec.employee) &&
        r.shift_date === rec.shift_date &&
        r.section === rec.section &&
        !seen.has(r.id)
      ) {
        seen.add(r.id);
        res.push(r);
      }
    }
    return res;
  }

  function isRecCell(cell, rec) {
    return (
      String(cell.props?.employeeId) === String(rec.employee) &&
      cell.props?.shiftDate === rec.shift_date &&
      cell.props?.sectionId === rec.section
    );
  }

  const cellsDataRef = useRef(new Map());
  const employeesMetaRef = useRef(new Map());
  const dataKeyRef = useRef(null);
  const storeIds = useSignal([]);
  const cellRecs = useSignal([]);
  const activeTab = useSignal(0);
  const recordId = useSignal(null);
  const btnDisabled = useSignal(true);
  const employeeRows = getEmployeeRows(records);
  const rows = getRowsSchema(employeeRows);
  const columns = getColumnsSchema(headDays);
  const [cells, setCells] = useState(() => getCells());
  useEffect(() => {
    if (dataKeyRef.current !== data) setCells(getCells());
  }, [data]);
  const mapShifts = new Map();

  for (const [recId, value] of shifts.entries()) {
    mapShifts.set(value.code, recId);
  }

  function validateShift(newValue) {
    const shiftId = mapShifts.get(newValue);
    if (!shiftId) return false;
    return shiftId;
  }

  function handleFill() {}

  function handleClose() {
    openModal.value = false;
    storeIds.value = [];
    cellRecs.value = [];
    activeTab.value = 0;
    recordId.value = null;
    btnDisabled.value = true;
  }

  function onAction(action, recordId) {
    if (action === "delete") {
      handleDelete(recordId);
    }
    openModal.value = false;
  }

  async function openSelected() {
    if (recordId.value < 0) return;
    const rec = cellsDataRef.current.get(recordId.value);
    if (!rec) return;

    let shiftsList = [];
    const shiftDomain = [
      ["employee", "=", rec.employee],
      ["shift_date", "=", rec.shift_date],
      ["state", "!=", "cancelled"],
    ];
    if (rec.section) shiftDomain.push(["section", "=", rec.section]);
    const { data: fresh, error: searchError } = await proxy.search(
      "staff.shift",
      shiftDomain,
      ["id", "shift_kind", "state", "position"],
    );
    shiftsList = !searchError && fresh?.length ? fresh : getCellShifts(rec);
    shiftsList = shiftsList
      .map((s) => ({
        ...s,
        shift_kind:
          s.shift_kind && typeof s.shift_kind === "object"
            ? s.shift_kind.id ?? s.shift_kind[0]
            : s.shift_kind,
      }))
      .filter((s) => shifts.has(s.shift_kind));

    cellRecs.value = shiftsList;
    const ids = shiftsList.map((s) => s.id).filter((id) => id > 0);
    if (!ids.length) return;
    const _storeIds = [];
    for (const id of ids) {
      const sid = await insertRecord(id, "staff.shift", layoutShift);
      if (sid) _storeIds.push(sid);
    }
    storeIds.value = _storeIds;
    activeTab.value = 0;
    openModal.value = true;
  }

  async function onSelectCell(recId) {
    if (recId < 0) {
      btnDisabled.value = true;
      return;
    }

    btnDisabled.value = false;
    recordId.value = recId;
  }

  async function onDoubleClick(recId) {
    if (recId < 0) return;
    const rec = cellsDataRef.current.get(recId);
    if (rec && !validateEmployee(rec)) return;
    btnDisabled.value = false;
    recordId.value = recId;
    openSelected();
  }

  function validateEmployee(rec) {
    if (!rec?.employee) return true;
    const meta = employeesMetaRef.current.get(String(rec.employee));
    if (!meta) return true;
    if (meta.is_outsourcing) {
      return true;
    }
    if (!meta.has_contract) {
      revertCell(rec);
      warning("El empleado no cuenta con contrato activo.");
      return false;
    }
    if (meta.finished_date && rec.shift_date > meta.finished_date) {
      revertCell(rec);
      warning(
        `El empleado tiene fecha de desvinculación el ${meta.finished_date}. No se puede programar.`,
      );
      return false;
    }
    return true;
  }

  function revertCell(rec) {
    const cellShifts = getCellShifts(rec);
    const kinds = sortKindIds(
      cellShifts.map((r) => r.shift_kind).filter(Boolean),
    );
    const firstKindId = kinds[0] || null;
    const shiftKind = shifts.get(firstKindId);
    const isWork = shiftKind?.mode ? shiftKind.mode === "work" : true;
    setCells((prev) =>
      refreshWeeklyTotals(
        prev.map((c) =>
          isRecCell(c, rec)
            ? {
                ...c,
                props: {
                  ...c.props,
                  text: getShiftCodes(kinds),
                  shifts: kinds.map((k) => shifts.get(k)).filter(Boolean),
                  style: firstKindId
                    ? {
                        ...c.baseStyle,
                        backgroundColor: isWork
                          ? getColor(firstKindId)
                          : "#e2e8f0",
                      }
                    : c.baseStyle,
                },
              }
            : c,
        ),
        records,
        cellsDataRef.current,
        shifts,
        weekHoursWork,
        mealMap,
      ),
    );
  }

  async function saveCell(rec, changes, oldCellId) {
    const isValid = validateEmployee(rec);
    if (!isValid) {
      return;
    }
    const staffConfig = getStorage("staffConfig");
    if (
      !isChangeShiftWithinLimit(
        rec?.shift_date,
        staffConfig?.change_shift_limit,
      )
    ) {
      revertCell(rec);
      error(
        "No está permitido modificar turnos para la fecha seleccionada según el límite configurado.",
      );
      return;
    }
    if (!rec.section) {
      revertCell(rec);
      warning("Debe seleccionar una sección para programar o modificar turnos.");
      return;
    }
    if (!rec.position) {
      const meta = employeesMetaRef.current.get(String(rec.employee));
      if (meta?.position) {
        rec.position = meta.position;
      }
    }

    const { toUpdate = [], toCancel = [], toCreate = [] } = changes;
    const wasEmpty = oldCellId < 0;
    const createdCodes = [];
    const updatedCodes = [];
    const cancelledCodes = [];
    try {
      const cancelIds = toCancel.map((r) => r.id).filter((id) => id > 0);
      if (cancelIds.length) {
        const res = await proxy.buttonMethod({
          model: "staff.shift",
          method: "cancel",
          ids: cancelIds,
        });
        if (res?.error) {
          throw new Error(
            res?.error?.error || res?.error?.message || String(res?.error),
          );
        }
        for (const r of toCancel) {
          if (r.id > 0) {
            cellsDataRef.current.delete(r.id);
            removedIdsRef.current.add(r.id);
          }
          cancelledCodes.push(shifts.get(r.shift_kind)?.code || r.shift_kind);
        }
      }

      for (const upd of toUpdate) {
        if (upd.rec.shift_kind === upd.newKind) continue;
        const prevKind = upd.rec.shift_kind;
        const res = await onSave({ ...upd.rec, shift_kind: upd.newKind });
        if (!res?.id) {
          throw new Error(res?.error || "No se pudo actualizar el turno");
        }
        upd.rec.shift_kind = upd.newKind;
        updatedCodes.push(shifts.get(upd.newKind)?.code || upd.newKind);
      }

      if (rec.id < 0) {
        const firstKind = toCreate.shift();
        if (firstKind) {
          rec.shift_kind = firstKind;
          const res = await onSave(rec);
          if (!res?.id) {
            throw new Error(res?.error || "No se pudo guardar el turno");
          }
          createdCodes.push(shifts.get(firstKind)?.code || firstKind);
          cellsDataRef.current.delete(oldCellId);
          rec.id = res.id;
          cellsDataRef.current.set(res.id, rec);
          setCells((prev) =>
            refreshWeeklyTotals(
              prev.map((c) =>
                c.id === oldCellId
                  ? {
                      ...c,
                      id: res.id,
                      props: {
                        ...c.props,
                        onDoubleClick: () => onDoubleClick(res.id),
                        onSelectCell: () => onSelectCell(res.id),
                      },
                    }
                  : c,
              ),
              records,
              cellsDataRef.current,
              shifts,
              weekHoursWork,
              mealMap,
            ),
          );
        }
      }

      for (const kindId of toCreate) {
        negId -= 1;
        const newRec = {
          id: negId,
          state: "draft",
          employee: rec.employee,
          shift_date: rec.shift_date,
          position: rec.position,
          shift_kind: kindId,
          section: rec.section,
        };
        const res = await onSave(newRec);
        if (!res?.id) {
          throw new Error(res?.error || "No se pudo guardar el turno");
        }
        createdCodes.push(shifts.get(kindId)?.code || kindId);
        newRec.id = res.id;
        cellsDataRef.current.set(res.id, newRec);
      }

      if (!wasEmpty && !cellsDataRef.current.has(oldCellId)) {
        const survivors = getCellShifts(rec);
        const kinds = sortKindIds(
          survivors.map((r) => r.shift_kind).filter(Boolean),
        );
        if (kinds.length) {
          const survivor = survivors.find((r) => r.shift_kind === kinds[0]);
          rec.id = survivor.id;
          rec.shift_kind = survivor.shift_kind;
          setCells((prev) =>
            refreshWeeklyTotals(
              prev.map((c) =>
                c.id === oldCellId
                  ? {
                      ...c,
                      id: survivor.id,
                      props: {
                        ...c.props,
                        onDoubleClick: () => onDoubleClick(survivor.id),
                        onSelectCell: () => onSelectCell(survivor.id),
                      },
                    }
                  : c,
              ),
              records,
              cellsDataRef.current,
              shifts,
              weekHoursWork,
              mealMap,
            ),
          );
        } else {
          negId -= 1;
          const newId = negId;
          rec.id = newId;
          rec.state = "draft";
          rec.shift_kind = null;
          cellsDataRef.current.set(newId, rec);
          setCells((prev) =>
            refreshWeeklyTotals(
              prev.map((c) =>
                c.id === oldCellId
                  ? {
                      ...c,
                      id: newId,
                      props: {
                        ...c.props,
                        text: "",
                        shifts: [],
                        style: c.baseStyle,
                        onDoubleClick: () => onDoubleClick(newId),
                        onSelectCell: () => onSelectCell(newId),
                      },
                    }
                  : c,
              ),
              records,
              cellsDataRef.current,
              shifts,
              weekHoursWork,
              mealMap,
            ),
          );
        }
      }

      const parts = [];
      if (createdCodes.length) {
        parts.push(`Turnos agregados: ${createdCodes.join(", ")}`);
      }
      if (updatedCodes.length) {
        parts.push(`Turnos actualizados: ${updatedCodes.join(", ")}`);
      }
      if (cancelledCodes.length) {
        parts.push(`Turnos cancelados: ${cancelledCodes.join(", ")}`);
      }
      if (parts.length) {
        success(parts.join(" · "));
      }
      setCells((prev) =>
        refreshWeeklyTotals(
          prev,
          records,
          cellsDataRef.current,
          shifts,
          weekHoursWork,
          mealMap,
        ),
      );
    } catch (errMsg) {
      if (rec.id < 0) {
        rec.shift_kind = null;
      }
      revertCell(rec);
      error(errMsg?.message || String(errMsg));
    }
  }

  async function cancelCell(rec) {
    const oldCellId = rec.id;
    const cellShifts = getCellShifts(rec);
    const ids = cellShifts.map((r) => r.id).filter((id) => id > 0);
    let newId = oldCellId;
    if (ids.length) {
      const args = {
        model: "staff.shift",
        method: "cancel",
        ids: ids,
      };
      const res = await proxy.buttonMethod(args);
      if (res?.error) {
        error(res?.error?.error || res?.error?.message || String(res?.error));
        return;
      }
      const cancelledCodes = cellShifts
        .map((r) => shifts.get(r.shift_kind)?.code)
        .filter(Boolean);
      for (const r of cellShifts) {
        cellsDataRef.current.delete(r.id);
        removedIdsRef.current.add(r.id);
      }
      negId -= 1;
      newId = negId;
      rec.id = newId;
      rec.state = "draft";
      rec.shift_kind = null;
      cellsDataRef.current.set(newId, rec);
      if (cancelledCodes.length) {
        success(`Turnos cancelados: ${cancelledCodes.join(", ")}`);
      }
    }
    setCells((prev) =>
      refreshWeeklyTotals(
        prev.map((c) =>
          c.id === oldCellId
            ? {
                ...c,
                id: newId,
                props: {
                  ...c.props,
                  text: "",
                  shifts: [],
                  style: c.baseStyle,
                  onDoubleClick: () => onDoubleClick(newId),
                  onSelectCell: () => onSelectCell(newId),
                },
              }
            : c,
        ),
        records,
        cellsDataRef.current,
        shifts,
        weekHoursWork,
        mealMap,
      ),
    );
    recordId.value = null;
    btnDisabled.value = true;
  }

  async function handleChange(rec, field, newValue) {
    const commitKey = `${rec.id}-${rec.shift_date}-${String(newValue)}`;
    if (lastCommitRef.current === commitKey) return;
    lastCommitRef.current = commitKey;
    try {
      await applyChange(rec, newValue);
    } finally {
      if (lastCommitRef.current === commitKey) lastCommitRef.current = null;
    }
  }

  async function applyChange(rec, newValue) {
    if (!validateEmployee(rec)) {
      return;
    }
    const staffConfig = getStorage("staffConfig");
    if (!isChangeShiftWithinLimit(rec?.shift_date, staffConfig?.change_shift_limit)) {
      warning("No está permitido modificar turnos para la fecha seleccionada según el límite configurado.");
      return;
    }
    if (!rec.section) {
      revertCell(rec);
      warning("Debe seleccionar una sección para programar o modificar turnos.");
      return;
    }
    const text = String(newValue);
    if (text === "0") {
      await cancelCell(rec);
      return;
    }
    if (!text) return;

    const isAdd = text.startsWith("-");
    const codes = text.split("-").map((c) => c.trim()).filter(Boolean);
    if (!codes.length) return;
    const typedIds = [];
    for (const code of codes) {
      const shiftId = validateShift(code);
      if (!shiftId) return;
      if (!typedIds.includes(shiftId)) typedIds.push(shiftId);
    }

    const cellShifts = getCellShifts(rec);
    const existingKinds = sortKindIds(
      cellShifts.map((r) => r.shift_kind).filter(Boolean),
    );
    const oldCellId = rec.id;

    let resultingKinds;
    let changes;
    if (isAdd) {
      const newKinds = typedIds.filter((id) => !existingKinds.includes(id));
      resultingKinds = sortKindIds([...existingKinds, ...newKinds]);
      changes = { toUpdate: [], toCancel: [], toCreate: newKinds };
    } else {
      const toUpdate = [];
      const toCancel = [];
      const toCreate = [];
      const exCount = existingKinds.length;
      const tyCount = typedIds.length;
      if (tyCount >= exCount) {
        for (let i = 0; i < exCount; i++) {
          if (existingKinds[i] !== typedIds[i]) {
            const updRec = cellShifts.find(
              (r) => r.shift_kind === existingKinds[i],
            );
            if (updRec) {
              toUpdate.push({ rec: updRec, newKind: typedIds[i] });
            }
          }
        }
        for (let i = exCount; i < tyCount; i++) {
          toCreate.push(typedIds[i]);
        }
      } else {
        const offset = exCount - tyCount;
        for (let i = 0; i < tyCount; i++) {
          if (existingKinds[offset + i] !== typedIds[i]) {
            const updRec = cellShifts.find(
              (r) => r.shift_kind === existingKinds[offset + i],
            );
            if (updRec) {
              toUpdate.push({ rec: updRec, newKind: typedIds[i] });
            }
          }
        }
        for (const r of cellShifts) {
          if (existingKinds.indexOf(r.shift_kind) < offset) {
            toCancel.push(r);
          }
        }
      }
      resultingKinds = sortKindIds(typedIds);
      changes = { toUpdate, toCancel, toCreate };
    }

    const firstKindId = resultingKinds[0] || null;
    const shiftKind = shifts.get(firstKindId);
    const isWork = shiftKind?.mode ? shiftKind.mode === "work" : true;
    const bgColor = isWork ? getColor(firstKindId) : "#e2e8f0";

    setCells((prev) =>
      refreshWeeklyTotals(
        prev.map((cell) => {
          if (cell.id !== rec.id) return cell;
          return {
            ...cell,
            props: {
              ...cell.props,
              text: firstKindId ? getShiftCodes(resultingKinds) : "",
              shifts: firstKindId
                ? resultingKinds.map((k) => shifts.get(k)).filter(Boolean)
                : [],
              style: firstKindId
                ? { ...cell.baseStyle, backgroundColor: bgColor }
                : cell.baseStyle,
            },
          };
        }),
        records,
        cellsDataRef.current,
        shifts,
        weekHoursWork,
        mealMap,
      ),
    );

    await saveCell(rec, changes, oldCellId);
  }

  function handleDelete(recordId) {
    const rec = cellsDataRef.current.get(recordId);
    if (!rec) return;
    removedIdsRef.current.add(recordId);
    const cellShifts = getCellShifts(rec).filter((r) => r.id !== recordId);
    cellsDataRef.current.delete(recordId);
    setCells((prev) =>
      refreshWeeklyTotals(
        prev.map((cell) => {
          if (!isRecCell(cell, rec)) {
            return cell;
          }
          if (!cellShifts.length) {
            negId -= 1;
            const recNull = {
              id: negId,
              state: "draft",
              employee: rec.employee,
              shift_date: rec.shift_date,
              position: rec.position,
              shift_kind: null,
              section: rec.section,
            };
            cellsDataRef.current.set(negId, recNull);
            return {
              ...cell,
              id: negId,
              props: {
                ...cell.props,
                text: "",
                shifts: [],
                style: cell.baseStyle,
                onTextChanged: (newText) =>
                  handleChange(recNull, "text", newText),
              },
            };
          }
          const sorted = sortKindIds(cellShifts.map((r) => r.shift_kind));
          const newMainId = cellShifts.find(
            (r) => r.shift_kind === sorted[0],
          ).id;
          rec.id = newMainId;
          rec.shift_kind = sorted[0];
          const shiftKind = shifts.get(sorted[0]);
          const isWork = shiftKind?.mode ? shiftKind.mode === "work" : true;
          const bgColor = isWork ? getColor(sorted[0]) : "#e2e8f0";
          return {
            ...cell,
            id: newMainId,
            props: {
              ...cell.props,
              text: getShiftCodes(sorted),
              shifts: sorted.map((k) => shifts.get(k)).filter(Boolean),
              style: { ...cell.baseStyle, backgroundColor: bgColor },
              onDoubleClick: () => onDoubleClick(newMainId),
              onSelectCell: () => onSelectCell(newMainId),
              onTextChanged: (newText) =>
                handleChange(rec, "text", newText),
            },
          };
        }),
        records,
        cellsDataRef.current,
        shifts,
        weekHoursWork,
        mealMap,
      ),
    );
    recordId.value = null;
    btnDisabled.value = true;
  }

  function getCells() {
    if (dataKeyRef.current !== data) {
      dataKeyRef.current = data;
      cellsDataRef.current = new Map();
      employeesMetaRef.current = new Map();
    }
    const res = [];
    let rowIdx = 0;
    const styleEmployee = style.employee;
    const styleEmployeeBg = {
      ...style.employee,
      backgroundColor: "rgb(241 245 249)",
    };
    const styleCellData = style.shift;
    const styleCellDataBg = {
      ...style.shift,
      backgroundColor: "rgb(241 245 249)",
    };
    setHeaderMonth(headMonths, rowIdx, res);
    rowIdx += 1;
    setHeaderRow(headDays, rowIdx, res, holidays);
    const extraById = new Map(extraShifts.map((s) => [s.id, s]));
    let rowBg = 0;
    let lastEmployeeId = null;
    for (const { row, sectionId, sectionName } of employeeRows) {
      let colIdx = 0;
      rowIdx += 1;
      let _style;
      let _styleCellData;
      const rowValues = Object.values(row);
      const firstValue = row.employee || rowValues[0];
      if (lastEmployeeId !== null && firstValue[0] !== lastEmployeeId) {
        rowBg += 1;
      }
      lastEmployeeId = firstValue[0];
      if (rowBg % 2 === 0) {
        _style = styleEmployeeBg;
        _styleCellData = styleCellDataBg;
      } else {
        _style = styleEmployee;
        _styleCellData = styleCellData;
      }
      res.push({
        rowIndex: rowIdx,
        colIndex: colIdx,
        Template: EmployeeCell,
        props: {
          value: firstValue[1],
          state: firstValue[2],
          style: _style,
        },
      });
      colIdx += 1;
      res.push({
        rowIndex: rowIdx,
        colIndex: colIdx,
        Template: NonEditableCell,
        props: {
          value: sectionName,
          style: _style,
        },
      });
      const employeeId = firstValue[0];
      const contractingState = firstValue[2];
      const employeePosition = firstValue[3] ? Number(firstValue[3]) : null;
      const finishedDate = firstValue[4] || null;
      const hasContract = Boolean(firstValue[5]);
      const isOutsourcing = contractingState === "outsourcing";

      employeesMetaRef.current.set(String(employeeId), {
        contracting_state: contractingState,
        finished_date: finishedDate,
        has_contract: hasContract,
        position: employeePosition,
        is_outsourcing: isOutsourcing,
      });

      let lastPosition = employeePosition;
      for (const day of headDays) {
        const isBlocked =
          !isOutsourcing && Boolean(finishedDate && day > finishedDate);
        let id = negId;
        let value = "";
        let state = "draft";
        let position = lastPosition;
        let shiftIdForColor = null;
        let shiftKind = null;
        let isWork = true;
        let shiftsOfDay = [];
        const rowData = isBlocked ? null : row[day];
        const dayShifts = !isBlocked && (rowData || extraShifts?.length)
          ? getDayShifts(employeeId, day, rowData, sectionId, extraById)
          : [];
        if (dayShifts.length) {
          const sortedKinds = sortKindIds(
            dayShifts.map((s) => s.shift_kind),
          );
          const firstShift = dayShifts.find(
            (s) => s.shift_kind === sortedKinds[0],
          );
          id = firstShift.id;
          state = firstShift.state;
          position = firstShift.position;
          lastPosition = position;
          shiftIdForColor = sortedKinds[0];
          shiftKind = shifts.get(shiftIdForColor);
          isWork = shiftKind?.mode ? shiftKind.mode === "work" : true;
          shiftsOfDay = sortedKinds
            .map((k) => shifts.get(k))
            .filter(Boolean);
          value = shiftsOfDay.map((s) => s.code).join("-");
        }
        negId -= 1;
        const jsDay = dates.getTrytonDate2Js(day);
        const isSunday = jsDay?.getDay() === 0;

        const rec = {
          id: id,
          state: state,
          employee: employeeId,
          shift_date: day,
          position: position,
          shift_kind: shiftIdForColor,
          section: sectionId,
        };
        cellsDataRef.current.set(id, rec);
        for (const s of dayShifts) {
          if (s.id === id) continue;
          cellsDataRef.current.set(s.id, {
            id: s.id,
            state: s.state,
            employee: employeeId,
            shift_date: day,
            position: s.position,
            shift_kind: s.shift_kind,
            section: sectionId,
          });
        }
        colIdx += 1;

        let weeklyTotal = null;
        if (isSunday) {
          weeklyTotal = calculateEmployeeWeekHours(
            employeeId,
            day,
            records,
            cellsDataRef.current,
            shifts,
            mealMap,
          );
        }

        const bgColor = isWork ? getColor(shiftIdForColor) : "#e2e8f0";
        const cellStyle = isBlocked
          ? {
              ..._styleCellData,
              backgroundColor: "#e2e8f0",
              cursor: "not-allowed",
            }
          : shiftIdForColor && value
            ? { ..._styleCellData, backgroundColor: bgColor }
            : _styleCellData;

        res.push({
          rowIndex: rowIdx,
          colIndex: colIdx,
          Template: CellData,
          id: id,
          baseStyle: isBlocked
            ? { ..._styleCellData, backgroundColor: "#e2e8f0", cursor: "not-allowed" }
            : _styleCellData,
          props: {
            text: isBlocked ? "" : value,
            hasEvent: isBlocked ? false : getDayEvents(employeeId, day).length > 0,
            events: isBlocked ? [] : getDayEvents(employeeId, day),
            shifts: isBlocked ? [] : shiftsOfDay,
            isSunday: isSunday,
            weeklyTotal: weeklyTotal,
            weekHoursLimit: weekHoursWork,
            employeeId: employeeId,
            shiftDate: day,
            sectionId: sectionId,
            isBlocked: isBlocked,
            style: cellStyle,
            onTextChanged: isBlocked
              ? undefined
              : (newText) => handleChange(rec, "text", newText),
            onDoubleClick: isBlocked ? undefined : () => onDoubleClick(id),
            onSelectCell: () => onSelectCell(id),
          },
        });
      }
    }
    return res;
  }

  async function onOk() {
    let error = null;
    for (const sid of storeIds.value) {
      const result = await saveForm(sid);
      if (result?.type === "error") {
        error = result;
        break;
      }
    }
    openModal.value = false;
    if (error) return;

    const record = useStore.getState()[storeIds.value[0]];
    const rec = record?.active;
    if (!rec) return;

    const shiftCode = rec.shift_kind?.code;
    const shiftId = rec.shift_kind?.id;
    if (!shiftCode) return;

    const cellRec = cellsDataRef.current.get(rec.id);
    if (cellRec) {
      cellRec.shift_kind = shiftId;
    }

    setCells((prev) =>
      refreshWeeklyTotals(
        prev.map((cell) => {
          if (cell.id !== rec.id) return cell;
          const allKinds = sortKindIds(
            getCellShifts(cellRec || rec)
              .map((r) => r.shift_kind)
              .filter(Boolean),
          );
          const firstKindId = allKinds[0] || null;
          const shiftKind = shifts.get(firstKindId);
          const isWork = shiftKind?.mode ? shiftKind.mode === "work" : true;
          const bgColor = isWork ? getColor(firstKindId) : "#e2e8f0";

          return {
            ...cell,
            props: {
              ...cell.props,
              text: getShiftCodes(allKinds),
              shifts: allKinds.map((k) => shifts.get(k)).filter(Boolean),
              style: firstKindId
                ? { ...cell.baseStyle, backgroundColor: bgColor }
                : cell.baseStyle,
            },
          };
        }),
        records,
        cellsDataRef.current,
        shifts,
        weekHoursWork,
        mealMap,
      ),
    );

    if (typeof refetch === "function") {
      await refetch();
    }
  }

  async function cancelActiveShift() {
    const target = cellRecs.value[activeTab.value];
    if (!target?.id || target.id < 0) return;
    const rec = cellsDataRef.current.get(target.id);
    const staffConfig = getStorage("staffConfig");
    if (
      !isChangeShiftWithinLimit(
        rec?.shift_date,
        staffConfig?.change_shift_limit,
      )
    ) {
      warning(
        "No está permitido modificar turnos para la fecha seleccionada según el límite configurado.",
      );
      return;
    }
    const res = await proxy.buttonMethod({
      model: "staff.shift",
      method: "cancel",
      ids: [target.id],
    });
    if (res?.error) {
      error(res?.error?.error || res?.error?.message || String(res?.error));
      return;
    }
    const code = shifts.get(target.shift_kind)?.code || target.shift_kind;
    handleClose();
    if (rec) {
      handleDelete(target.id);
    } else if (typeof refetch === "function") {
      await refetch();
    }
    success(`Turno cancelado: ${code}`);
  }

  async function handleButton(btn) {
    await btn.onClick();
  }

  const viewportWidth = document.documentElement.clientWidth;
  let styleId = "staff-shift-rg-content-15";
  if (viewportWidth < 450) {
    styleId = "staff-shift-rg-content-mobile";
  } else if (viewportWidth < 768) {
    styleId = "staff-shift-rg-content-tablet";
  } else if (selectedDays === 30) {
    styleId = "staff-shift-rg-content-30";
  }

  return (
    <div className="flex flex-col w-full h-full" id={styleId}>
      <div className="flex flex-row items-center justify-between md:hidden px-2 mb-3 mt-1 gap-3">
        <div className="flex items-center justify-center gap-2 flex-1 bg-white border border-neutral-200 rounded-lg p-2 shadow-sm">
          <ShiftList shifts={shifts} />
          <span className="text-sm font-semibold text-neutral-600">
            Info. Turnos
          </span>
        </div>
        <button
          type="button"
          disabled={btnDisabled.value}
          onClick={openSelected}
          className={cn(
            "p-2 flex-1 rounded-lg shadow-sm font-semibold text-sm transition-all active:scale-95",
            btnDisabled.value
              ? "bg-neutral-200 text-neutral-400 cursor-not-allowed border border-neutral-200"
              : "bg-amber-400 text-neutral-900 shadow-md border border-amber-500",
          )}
        >
          Modificar
        </button>
      </div>
      <div className="flex-1 min-h-0 w-full overflow-auto">
        <ReactGrid
          rows={rows}
          columns={columns}
          cells={cells}
          onFillHandle={handleFill}
          stickyTopRows={2}
        />
      </div>
      <MessageContainer messages={messages} onMessageClose={hide} />
      {openModal.value && (
        <Dialog
          openModal={openModal}
          title="staff.shift.form"
          handleClose={handleClose}
          handleActionButton={handleButton}
          buttons={[
            { name: "cancel", onClick: handleClose },
            ...(storeIds.value.length > 1
              ? [
                  {
                    name: "cancel_shift",
                    label: "staff.shift.cancel_shift",
                    color: "rose",
                    onClick: cancelActiveShift,
                  },
                ]
              : []),
            { name: "ok", onClick: onOk },
          ]}
        >
          {layoutShift && storeIds.value.length > 0 && (
            <div>
              {storeIds.value.length > 1 && (
                <div className="flex flex-wrap items-center gap-2 mb-3">
                  {cellRecs.value.map((s, i) => {
                    const kind = shifts.get(s.shift_kind);
                    const label = kind
                      ? `${kind.code} · ${kind.start ?? ""}-${kind.end ?? ""}`
                      : `Turno ${i + 1}`;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        onClick={() => (activeTab.value = i)}
                        className={cn(
                          "px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all",
                          activeTab.value === i
                            ? "bg-amber-400 text-neutral-900 border-amber-500 shadow-sm"
                            : "bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50",
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              )}
              <ShiftForm
                storeId={storeIds.value[activeTab.value]}
                onAction={onAction}
                layout={layoutShift}
              />
            </div>
          )}
        </Dialog>
      )}
    </div>
  );
}

const style = {
  employee: {
    color: "#334155",
    display: "flex",
    justifyContent: "left",
    paddingLeft: "6px",
    fontSize: "0.875rem",
    fontWeight: "500",
    textOverflow: "ellipsis",
  },
  shift: {
    color: "#475569",
    display: "flex",
    justifyContent: "center",
    paddingRight: "5px",
    fontSize: "0.875rem",
    fontWeight: "600",
  },
};

export default Sheet;
